import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  Input,
  SecurityContext,
  Output,
  EventEmitter,
  Inject,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/**
 * Component to embedded a remotely hosted React application as an Angular component.
 *
 * The React application must expose a global mount/unmount API:
 *
 * ```typescript
 * window.ReactAppLoader = {
 *   mount: (container, props = {}) => {
 *     const root = ReactDOM.createRoot(container);
 *     root.render(<App {...props} />);
 *     return root;
 *   },
 *   unmount: (root) => {
 *     root.unmount();
 *   }
 * };
 * ```
 *
 * @example
 * ```html
 * <miq-react-embedded
 *   [reactAppUrl]="'https://your-react-app.com'"
 *   [scriptPath]="'/static/js/main.js'"
 *   [cssPath]="'/static/css/main.css'"
 *   [props]="{ userId: 123, theme: 'dark' }">
 * </miq-react-embedded>
 * ```
 */
@Component({
  selector: 'miq-react-embedded',
  template: '<div #reactContainer class="react-container"></div>',
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
      .react-container {
        height: 100%;
        width: 100%;
        float: right;

        // Temporary fix for React apps aside positioning
        aside {
          left: 60px !important;
          top: 155px !important;
        }
      }
    `,
  ],
  imports: [CommonModule],
})
export class ReactEmbeddedComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('reactContainer', { static: false }) containerRef: ElementRef;

  /**
   * Base URL of the remotely hosted React application
   */
  @Input() reactAppUrl: string;

  /**
   * Path to the main JavaScript bundle (relative to reactAppUrl)
   * @default '/static/js/main.js'
   */
  @Input() scriptPath = '/static/js/main.js';

  /**
   * Path to the main CSS file (relative to reactAppUrl)
   * @default '/static/css/main.css'
   */
  @Input() cssPath = '/static/css/main.css';

  /**
   * Props to pass to the React application
   */
  @Input() props: any = {};

  /**
   * Name of the global loader object exposed by the React app
   * @default 'ReactAppLoader'
   */
  @Input() loaderName = 'ReactAppLoader';

  /**
   * Whether to load CSS automatically
   * @default true
   */
  @Input() loadCss = true;

  /**
   * Trigger Error to be handle by the parent component
   */
  @Output() handleError = new EventEmitter<void>();

  private reactRoot: any;
  private loadingPromise: Promise<void> | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // When props input changes after the React app is already mounted,
    // forward them via updateProps instead of remounting the whole app.
    if (changes['props'] && !changes['props'].firstChange && this.reactRoot) {
      const loader = (window as any)[this.loaderName];
      if (loader && typeof loader.updateProps === 'function') {
        try {
          this.props = changes['props'].currentValue;
          loader.updateProps(this.reactRoot, this.props);
        } catch {
          this.logger.error('ReactEmbeddedComponent: Failed to update props on change');
        }
      }
    }
  }

  ngAfterViewInit(): void {
    if (!this.reactAppUrl) {
      this.logger.error('ReactEmbeddedComponent: reactAppUrl is required');
      this.handleError.emit();
      return;
    }
    this.loadReactApp();
  }

  /**
   * Load the React application scripts and mount it
   */
  private async loadReactApp(): Promise<void> {
    // If already loading, wait for that to complete
    if (this.loadingPromise != null) {
      await this.loadingPromise;
      return;
    }

    // Check if already loaded
    if ((window as any)[this.loaderName]) {
      this.mountReactApp();
      return;
    }

    this.loadingPromise = this.loadScripts();
    await this.loadingPromise;
  }

  /**
   * Load required scripts and stylesheets
   */
  private async loadScripts(): Promise<void> {
    try {
      // Load CSS first
      if (this.loadCss && this.cssPath) {
        await this.loadStylesheet(`${this.reactAppUrl}${this.cssPath}`);
      }

      // Load JavaScript
      await this.loadScript(`${this.reactAppUrl}${this.scriptPath}`);

      this.mountReactApp();
    } catch {
      this.logger.error('ReactEmbeddedComponent: Failed to load React app');
      this.handleError.emit();
    } finally {
      this.loadingPromise = null;
    }
  }

  /**
   * Load a JavaScript file dynamically
   */
  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = this.sanitizer.sanitize(SecurityContext.URL, src);
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  /**
   * Load a CSS file dynamically
   */
  private loadStylesheet(href: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if stylesheet already exists
      const existingLink = document.querySelector(`link[href="${href}"]`);
      if (existingLink) {
        resolve();
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = this.sanitizer.sanitize(SecurityContext.URL, href);
      link.onload = () => resolve();
      link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
      document.head.appendChild(link);
    });
  }

  /**
   * Mount the React application
   */
  private mountReactApp(): void {
    let attemps = 0;
    const waitInt = setInterval(() => {
      if ((window as any)[this.loaderName]) {
        clearInterval(waitInt);

        const loader = (window as any)[this.loaderName];
        if (!loader || typeof loader.mount !== 'function') {
          this.logger.error(
            `ReactEmbeddedComponent: mount is not available. Make sure the React app exposes a global ReactAppLoader object with mount/unmount methods.`
          );
          return;
        }

        try {
          this.reactRoot = loader.mount(this.containerRef.nativeElement, this.props);
        } catch {
          this.logger.error('ReactEmbeddedComponent: Failed to mount React app');
          this.handleError.emit();
        }
      }
      attemps++;

      if (attemps > 50) clearInterval(waitInt);
    }, 100);
  }

  /**
   * Unmount the React application on component destroy
   */
  ngOnDestroy(): void {
    const loader = (window as any)[this.loaderName];
    if (this.reactRoot && loader && typeof loader.unmount === 'function') {
      try {
        loader.unmount(this.reactRoot);
      } catch {
        this.logger.error('ReactEmbeddedComponent: Failed to unmount React app');
        this.handleError.emit();
      }
    }
  }

  /**
   * Update props passed to the React application
   * Note: This requires the React app to support prop updates
   */
  updateProps(newProps: any): void {
    this.props = { ...this.props, ...newProps };
    const loader = (window as any)[this.loaderName];
    if (loader && typeof loader.updateProps === 'function' && this.reactRoot) {
      try {
        loader.updateProps(this.reactRoot, this.props);
      } catch {
        this.logger.error('ReactEmbeddedComponent: Failed to update props');
        this.handleError.emit();
      }
    }
  }
}
